// Chat Composer component for Inbox v2 (PRD §1.7, WP-04).
// Reply / Note tabs, emoji picker, "+" menu (templates, canned, files, AI rewrite, translate, scheduled), audio voice note recorder.
import { useState, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { useTenant } from '../../lib/tenant';
import { useAccounts } from '../../lib/accounts';
import { cn } from '../../lib/cn';
import { sendMessage, addConversationNote } from '../../lib/inbox';
import { uploadInboxMediaFile, uploadMediaToMeta, rewriteMessage, translateMessage } from '../../lib/media';
import { use24hWindow } from '../../lib/use24hWindow';
import type { LiveConversation } from '../../lib/inboxTypes';
import type { Product } from '../../lib/catalog';
import { useToast } from '../ui/Toast';
import AudioRecorder from './AudioRecorder';
import ScheduleTemplateModal from './ScheduleTemplateModal';
import CannedResponsesModal from './CannedResponsesModal';
import ScheduledListModal from './ScheduledListModal';
import SendProductModal from './SendProductModal';
import {
  SendIcon,
  PlusIcon,
  SmileIcon,
  MicIcon,
  PaperclipIcon,
  SparklesIcon,
  ClockIcon,
  FileTextIcon,
  CloseIcon,
} from '../icons';

interface ChatComposerProps {
  conv: LiveConversation;
  onMessageSent?: () => void;
}

const COMMON_EMOJIS = ['👍', '❤️', '😊', '😂', '🙏', '🔥', '🎉', '✨', '👋', '🌹', '✅', '📦', '⭐', '🤝'];

export default function ChatComposer({ conv, onMessageSent }: ChatComposerProps) {
  const { t } = useI18n();
  const { tenantId } = useTenant();
  const toast = useToast();
  const accounts = useAccounts();
  const activeIntegration =
    accounts.numbers.find((n) => n.id === conv.integrationId) ||
    accounts.selected ||
    accounts.numbers.find((n) => n.id === accounts.defaultId) ||
    null;
  const activeCatalogId = activeIntegration?.catalogId;

  const [activeTab, setActiveTab] = useState<'reply' | 'note'>('reply');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  // Attachment state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileCaption, setFileCaption] = useState('');
  const [asDocument, setAsDocument] = useState(false);

  // Audio recording mode
  const [isRecording, setIsRecording] = useState(false);

  // Popovers & Modals
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCannedModal, setShowCannedModal] = useState(false);
  const [showScheduledModal, setShowScheduledModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [sendingProduct, setSendingProduct] = useState(false);
  const [isRewriting, setIsRewriting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const windowState = use24hWindow(conv);
  const isLocked = (windowState.expired || windowState.noInbound) && activeTab === 'reply';

  const handleSend = async () => {
    const text = draft.trim();
    if (!text && !selectedFile) return;

    setSending(true);
    try {
      if (activeTab === 'note') {
        if (!text) return;
        await addConversationNote({ conversationId: conv.id, text });
        setDraft('');
      } else if (selectedFile && tenantId) {
        // Upload media file
        const { storagePath, mimeType } = await uploadInboxMediaFile(tenantId, selectedFile, selectedFile.name);
        const metaRes = await uploadMediaToMeta({
          storagePath,
          mimeType,
          asVoiceNote: false,
        });

        const kind = selectedFile.type.startsWith('image/')
          ? 'image'
          : selectedFile.type.startsWith('video/')
          ? 'video'
          : selectedFile.type.startsWith('audio/')
          ? 'audio'
          : 'document';

        await sendMessage({
          to: conv.phone,
          conversationId: conv.id,
          mediaId: metaRes.mediaId,
          mediaType: kind as any,
          caption: fileCaption.trim() || undefined,
          asDocument,
        });

        setSelectedFile(null);
        setFileCaption('');
        setAsDocument(false);
      } else if (text) {
        await sendMessage({
          to: conv.phone,
          conversationId: conv.id,
          body: text,
        });
        setDraft('');
      }
      onMessageSent?.();
    } catch (err) {
      toast.error((err as Error).message || t('inbox.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFileCaption('');
      setAsDocument(!file.type.startsWith('image/') && !file.type.startsWith('video/'));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    setPlusMenuOpen(false);
  };

  const handleVoiceNoteSent = async (mediaId: string) => {
    setIsRecording(false);
    try {
      await sendMessage({
        to: conv.phone,
        conversationId: conv.id,
        mediaId,
        mediaType: 'audio',
      });
      onMessageSent?.();
    } catch (err) {
      toast.error((err as Error).message || t('inbox.voiceNoteFailed'));
    }
  };

  const handleInsertEmoji = (emoji: string) => {
    setDraft((prev) => prev + emoji);
    setEmojiOpen(false);
    textareaRef.current?.focus();
  };

  const handleRewrite = async (tone: 'professional' | 'friendly' | 'concise' | 'persuasive' | 'casual') => {
    if (!draft.trim()) return;
    setIsRewriting(true);
    setAiMenuOpen(false);
    setPlusMenuOpen(false);
    try {
      const res = await rewriteMessage({ text: draft.trim(), tone });
      if (res.ok && res.rewritten) {
        setDraft(res.rewritten);
      }
    } catch (err) {
      toast.error((err as Error).message || t('inbox.rewriteFailed'));
    } finally {
      setIsRewriting(false);
    }
  };

  const handleTranslate = async () => {
    if (!draft.trim()) return;
    setIsTranslating(true);
    setPlusMenuOpen(false);
    try {
      // If draft contains mostly Arabic, translate to English, else Arabic
      const isArabic = /[\u0600-\u06FF]/.test(draft);
      const targetLanguage = isArabic ? 'English' : 'Arabic';
      const res = await translateMessage({ text: draft.trim(), targetLanguage });
      if (res.ok && res.translated) {
        setDraft(res.translated);
      }
    } catch (err) {
      toast.error((err as Error).message || t('inbox.translateFailed'));
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSendProduct = async (product: Product) => {
    if (!activeCatalogId) return;
    setSendingProduct(true);
    try {
      await sendMessage({
        to: conv.phone,
        conversationId: conv.id,
        integrationId: activeIntegration?.id,
        product: {
          catalogId: activeCatalogId,
          retailerId: product.id,
        },
      });
      onMessageSent?.();
    } catch (err) {
      toast.error((err as Error).message || t('inbox.productSendFailed'));
    } finally {
      setSendingProduct(false);
    }
  };

  return (
    <div className="border-t border-line bg-white dark:border-slate-800 dark:bg-slate-900 transition-colors">
      {/* Reply vs Note Tabs */}
      <div className="flex border-b border-line px-3 pt-1.5 dark:border-slate-800">
        <button
          type="button"
          onClick={() => setActiveTab('reply')}
          className={cn(
            'border-b-2 px-3 py-1 text-xs font-bold transition-colors',
            activeTab === 'reply'
              ? 'border-brand text-brand'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
          )}
        >
          {t('inbox.composer.tabReply') || 'Reply'}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('note')}
          className={cn(
            'border-b-2 px-3 py-1 text-xs font-bold transition-colors',
            activeTab === 'note'
              ? 'border-amber-500 text-amber-600 dark:text-amber-400'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
          )}
        >
          📝 {t('inbox.composer.tabNote') || 'Internal Note'}
        </button>
      </div>

      {/* 24h Window Locked State Banner */}
      {isLocked ? (
        <div className="p-4 bg-amber-50/70 border-b border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <b className="block text-xs font-bold text-amber-800 dark:text-amber-300">
                ⏱ {t('inbox.window.closedTitle') || '24-hour service window closed'}
              </b>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                {t('inbox.composer.lockedNote') ||
                  'Meta requires an approved message template to start or resume a conversation outside the 24h window.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowTemplateModal(true)}
              className="flex items-center justify-center gap-1.5 rounded-xl bg-amber-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-amber-700 shrink-0"
            >
              <SparklesIcon size={14} />
              <span>{t('inbox.composer.pickTemplate') || 'Choose Template'}</span>
            </button>
          </div>
        </div>
      ) : isRecording ? (
        /* Live Audio Voice Recorder */
        <div className="p-3">
          <AudioRecorder
            onCancel={() => setIsRecording(false)}
            onSend={handleVoiceNoteSent}
          />
        </div>
      ) : (
        /* Standard Composer Input Form */
        <div className="p-2.5 sm:p-3 space-y-2">
          {/* Attached File Preview Bar */}
          {selectedFile && (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-line bg-slate-50 p-2.5 dark:border-slate-800 dark:bg-slate-850">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-base shrink-0">📎</span>
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-bold text-ink dark:text-slate-100">
                    {selectedFile.name}
                  </span>
                  <span className="text-[10px] text-ink-3 dark:text-slate-400">
                    {(selectedFile.size / 1024).toFixed(0)} KB
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <label className="flex items-center gap-1.5 text-xs text-ink-2 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={asDocument}
                    onChange={(e) => setAsDocument(e.target.checked)}
                    className="h-3.5 w-3.5 rounded text-brand focus:ring-brand"
                  />
                  <span>{t('inbox.composer.asDocument') || 'Send as doc'}</span>
                </label>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="grid h-6 w-6 place-items-center rounded-full text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Main Input Controls Row */}
          <div
            className={cn(
              'flex items-end gap-1.5 sm:gap-2 rounded-2xl border p-1.5 transition-colors',
              activeTab === 'note'
                ? 'border-amber-300 bg-amber-50/40 dark:border-amber-900/60 dark:bg-amber-950/20'
                : 'border-line bg-surface dark:border-slate-800 dark:bg-slate-850',
            )}
          >
            {/* "+" Menu Button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setPlusMenuOpen((v) => !v);
                  setEmojiOpen(false);
                  setAiMenuOpen(false);
                }}
                title={t('inbox.composer.actions') || 'Actions & Attachments'}
                className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-slate-750"
              >
                <PlusIcon size={17} />
              </button>

              {plusMenuOpen && (
                <div className="absolute bottom-full start-0 z-40 mb-2 w-56 overflow-hidden rounded-xl border border-line bg-white py-1 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
                  {/* Send Message Template */}
                  <button
                    type="button"
                    onClick={() => {
                      setPlusMenuOpen(false);
                      setShowTemplateModal(true);
                    }}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <SparklesIcon size={15} className="text-brand" />
                    <span>{t('inbox.composer.sendTemplate') || 'Message Template'}</span>
                  </button>

                  {/* Canned Response */}
                  <button
                    type="button"
                    onClick={() => {
                      setPlusMenuOpen(false);
                      setShowCannedModal(true);
                    }}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <FileTextIcon size={15} className="text-amber-500" />
                    <span>{t('inbox.composer.cannedResponse') || 'Canned Response'}</span>
                  </button>

                  {/* Attach File */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <PaperclipIcon size={15} className="text-blue-500" />
                    <span>{t('inbox.composer.attachFile') || 'Attach File / Photo'}</span>
                  </button>

                  {/* Send Product (WP-21b / WP-22) */}
                  {activeCatalogId && (
                    <button
                      type="button"
                      onClick={() => {
                        setPlusMenuOpen(false);
                        setShowProductModal(true);
                      }}
                      className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <span className="text-sm">🛍️</span>
                      <span>{t('inbox.composer.sendProduct') || 'Send Product'}</span>
                    </button>
                  )}

                  {/* Rewrite with AI */}
                  <div className="relative border-t border-line dark:border-slate-800">
                    <button
                      type="button"
                      onClick={() => setAiMenuOpen((v) => !v)}
                      className="flex w-full items-center justify-between px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-xs">✨</span>
                        <span>{t('inbox.composer.rewriteAi') || 'Rewrite with AI'}</span>
                      </div>
                      <span className="text-[10px] text-ink-4">›</span>
                    </button>

                    {aiMenuOpen && (
                      <div className="border-t border-line bg-slate-50 py-1 dark:border-slate-800 dark:bg-slate-850">
                        {(['professional', 'friendly', 'concise', 'persuasive', 'casual'] as const).map((tone) => (
                          <button
                            key={tone}
                            type="button"
                            onClick={() => void handleRewrite(tone)}
                            className="flex w-full px-5 py-1 text-xs capitalize text-slate-700 hover:bg-slate-200/50 dark:text-slate-200 dark:hover:bg-slate-800"
                          >
                            {tone}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Translate draft */}
                  <button
                    type="button"
                    onClick={() => void handleTranslate()}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <span className="text-xs">🌐</span>
                    <span>{t('inbox.composer.translateDraft') || 'Translate Draft'}</span>
                  </button>

                  {/* Scheduled Messages List */}
                  <button
                    type="button"
                    onClick={() => {
                      setPlusMenuOpen(false);
                      setShowScheduledModal(true);
                    }}
                    className="flex w-full items-center gap-2.5 border-t border-line px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <ClockIcon size={15} className="text-amber-500" />
                    <span>{t('inbox.composer.scheduledList') || 'Scheduled Messages'}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFilePicked}
              className="hidden"
            />

            {/* Emoji Picker Popover */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setEmojiOpen((v) => !v);
                  setPlusMenuOpen(false);
                }}
                title={t('inbox.composer.emoji') || 'Insert Emoji'}
                className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-slate-750"
              >
                <SmileIcon size={17} />
              </button>

              {emojiOpen && (
                <div className="absolute bottom-full start-0 z-40 mb-2 grid grid-cols-7 gap-1 rounded-xl border border-line bg-white p-2 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
                  {COMMON_EMOJIS.map((em) => (
                    <button
                      key={em}
                      type="button"
                      onClick={() => handleInsertEmoji(em)}
                      className="grid h-8 w-8 place-items-center rounded-lg text-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                    >
                      {em}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Textarea Input */}
            <div className="min-w-0 flex-1">
              <textarea
                ref={textareaRef}
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                placeholder={
                  activeTab === 'note'
                    ? t('inbox.composer.notePlaceholder') || 'Add an internal note visible only to your team…'
                    : selectedFile
                    ? t('inbox.composer.captionPlaceholder') || 'Add a caption (optional)…'
                    : t('inbox.composer.replyPlaceholder') || 'Type a message (Enter to send)…'
                }
                className="max-h-32 w-full resize-none bg-transparent px-2 py-1 text-xs text-ink outline-none dark:text-slate-100 placeholder:text-slate-400"
              />
            </div>

            {/* AI Rewriting / Translating indicator */}
            {(isRewriting || isTranslating) && (
              <span className="text-[10px] font-bold text-brand animate-pulse">
                {isRewriting ? 'Rewriting…' : 'Translating…'}
              </span>
            )}

            {/* Mic / Audio Record Button (only in reply tab and no draft) */}
            {activeTab === 'reply' && !draft.trim() && !selectedFile && (
              <button
                type="button"
                onClick={() => setIsRecording(true)}
                title={t('inbox.composer.recordVoice') || 'Record Voice Note'}
                className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-slate-750"
              >
                <MicIcon size={17} />
              </button>
            )}

            {/* Send Button */}
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={sending || (!draft.trim() && !selectedFile)}
              aria-label={t('common.send') || 'Send'}
              className={cn(
                'grid h-8 w-8 place-items-center rounded-full text-white shadow-sm transition disabled:opacity-40',
                activeTab === 'note'
                  ? 'bg-amber-500 hover:bg-amber-600'
                  : 'bg-brand hover:bg-brand-dark',
              )}
            >
              <SendIcon size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Modals rendered on demand */}
      {showTemplateModal && (
        <ScheduleTemplateModal
          conv={conv}
          onClose={() => setShowTemplateModal(false)}
          onSuccess={onMessageSent}
        />
      )}

      {showCannedModal && (
        <CannedResponsesModal
          activeConv={conv}
          onClose={() => setShowCannedModal(false)}
          onSelect={(text) => {
            setDraft((prev) => (prev ? prev + ' ' + text : text));
            textareaRef.current?.focus();
          }}
        />
      )}

      {showScheduledModal && (
        <ScheduledListModal
          conv={conv}
          onClose={() => setShowScheduledModal(false)}
        />
      )}

      {/* Send Product Modal (WP-21b / WP-22) */}
      {showProductModal && activeCatalogId && (
        <SendProductModal
          catalogId={activeCatalogId}
          onClose={() => setShowProductModal(false)}
          onSendProduct={handleSendProduct}
          sending={sendingProduct}
        />
      )}
    </div>
  );
}
